/* ===================================================================
   LOCKED APPEARANCE -- outdoor high-contrast, and nothing else.

   Steve, 2026-08-06 (verbatim): "I would like to opt out of the appearance
   checker or the appearance changer to go from light to dark to the really
   extreme high contrast appearance. I'd like it to only be the high
   contrast... the outdoor high contrast view. I don't want it to be
   anything but that."

   This replaces the entire Light/Dark/Outdoor theme system: ThemeProvider,
   themeContext, useTheme, ThemeToggle and AppearanceCard are all deleted.
   There is no context, no state, no setter, and no stored preference.

   LOCKED_THEME is a CONSTANT, not a setting. AppShell stamps it on .app so
   the ~300 swept per-screen overrides in theme.css keep matching without
   being rewritten (rewriting them is exactly where contrast bugs come from).
   Nothing reads it back and nothing can change it at runtime.

   It also matches the standing accessibility principle: the target user is
   an older tradesman reading a phone in daylight in a truck cab
   (feedback_rhody_reviews_app_accessibility_ui.md).
   =================================================================== */

/** The one and only appearance. Not user-selectable. */
export const LOCKED_THEME = 'outdoor' as const;

/** The key the retired theme switcher used to persist a Light/Dark/Outdoor
    choice. Kept ONLY so we can delete stale values. */
const LEGACY_THEME_STORAGE_KEY = 'rhody-theme';

/**
 * Erase any theme preference a returning owner still has in localStorage.
 *
 * Load-bearing: without this, an owner (or Steve) who previously chose
 * "dark" would keep a stale 'rhody-theme' value sitting in their browser.
 * Nothing reads it today, so it could not resurrect dark mode on its own,
 * but leaving it behind means a future reader could pick it up again and
 * silently un-lock the appearance. Delete it at boot so the retired setting
 * cannot come back.
 *
 * Safe to call on every boot: removeItem on a missing key is a no-op, and
 * storage access is wrapped (private mode / disabled storage just skips).
 */
export function purgeLegacyThemePreference(): void {
  try {
    localStorage.removeItem(LEGACY_THEME_STORAGE_KEY);
  } catch {
    /* storage unavailable (private mode / embedded webview) -- nothing to do */
  }
}
